import React, { useMemo } from "react";
import { Dropdown, DropdownItem, DropdownProps } from "../Dropdown/Dropdown";
import { useStore } from "zustand";
import globalStore from "../../../../state";
import { Feature, isFeatureAvailable } from "@functions/featureAvailability";
import { PremiumIndicator } from "../FeatureGating/PremiumIndicator";
import { useLegacySeatsData } from "../WorkspaceInvite/useLegacySeatsData";
import { DEFAULT_BASE_ROLE_OPTIONS, RoleOption } from "../TeamMember/roles";
import { WorkspaceCustomRole, WorkspaceRoleId } from "../../../../state/types";
const { isTrial } = require("../../../routes/workspace/utils");

export type RoleSelectorProps = {
    value: string;
    customOptions?: RoleOption[];
    onChange: (role: string) => void;
    label?: string;
    disabled?: boolean;
    className?: string;
} & Partial<DropdownProps>;

export function RoleSelector({
    value,
    customOptions,
    onChange,
    label,
    disabled = false,
    className,
    ...dropdownProps
}: RoleSelectorProps) {
    const { usage, workspace } = useStore(globalStore, (state) => ({
        usage: state.getUsage(),
        workspace: state.workspacesById?.[state.currentWorkspaceId || ""] || {}
    }));

    const { hasSeparateSeatLimits, rbacInfo } = useLegacySeatsData();
    const disabledRoles = [
        ...(hasSeparateSeatLimits && rbacInfo.labelerInvitesRemaining <= 0 ? ["labeler"] : []),
        ...(hasSeparateSeatLimits && rbacInfo.reviewerInvitesRemaining <= 0 ? ["reviewer"] : [])
    ];

    const hasRBAC = isFeatureAvailable(Feature.RoleBasedAccessControl, usage);
    const planIsTrial = isTrial(usage);

    const dropdownItems: DropdownItem[] = useMemo(() => {
        const customRoles: [WorkspaceRoleId, WorkspaceCustomRole][] = Object.entries(
            workspace?.customRoles || {}
        );
        const items = customOptions || [
            ...DEFAULT_BASE_ROLE_OPTIONS,
            ...customRoles.map(([roleId, role]) => ({ text: role.roleDisplayName, value: roleId }))
        ];

        return items.map((i) => {
            const role = i.value;
            const disabledDueToRBAC = !hasRBAC && role !== "owner";
            const isDisabled = disabled || disabledDueToRBAC || disabledRoles.includes(role);

            // Show premium indicator when RBAC is not available OR when user is on trial plan for labeler/reviewer roles
            const shouldShowIndicator = disabledDueToRBAC || (planIsTrial && role !== "owner");

            // Compose the label with an optional premium indicator when RBAC is not available
            const labelContent = (
                <span className="flex w-full items-center justify-between overflow-visible">
                    <span className="truncate">{i.text}</span>
                    {shouldShowIndicator && (
                        <PremiumIndicator
                            featureId={Feature.RoleBasedAccessControl}
                            showText={false}
                            indicatorType="feature"
                            className="ml-2 [&_i]:!text-inherit"
                        />
                    )}
                </span>
            );

            return {
                value: role,
                text: labelContent,
                disabled: isDisabled,
                menuItemParentClassName: "w-full overflow-visible"
            };
        });
    }, [disabled, hasRBAC, disabledRoles, customOptions, workspace, planIsTrial]);

    const currentSelectionText = dropdownItems.find((d) => d.value === value)?.text ?? "Select";

    return (
        <Dropdown
            theme="light"
            title={currentSelectionText as string}
            items={dropdownItems}
            onItemSelected={(item) => {
                if (item.disabled) return;
                onChange(item.value);
            }}
            autoSetTitle={false}
            fullWidth={true}
            menuButtonSize="medium"
            label={label}
            className={className}
            disabled={disabled}
            {...dropdownProps}
        />
    );
}

export default RoleSelector;
